import { AttachImage, ClassAvailableArea, ClassBadge } from './class';
import { ReactComponent as HomeIcon } from '@assets/images/app/icon-home.svg';
import { ReactComponent as OfficeIcon } from '@assets/images/app/icon-building.svg';
import { ReactComponent as OnlineIcon } from '@assets/images/app/icon-untact.svg';
import { ReactComponent as ExperienceIcon } from '@assets/images/app/icon-light.svg';
import { ReactComponent as DeliveryIcon } from '@assets/images/app/icon-truck.svg';
import { ReactComponent as TicketIcon } from '@assets/images/app/icon-ticket.svg';
import { ReactComponent as DownloadIcon } from '@assets/images/app/icon-download.svg';

import { NaverCommerceProduct } from './sales-channels';
import { Membership } from './membership';
import { IntelligenceType } from './programReport';

interface StatusText {
  text: string;
  textColor: string;
  backgroundColor: string;
}

export enum LegacyProductStatus {
  ALL = 'all',
  OPEN = 'open',
  CLOSE = 'close',
  HIDE = 'hide',
  DENIED = 'denied',
  OPEN_REQUEST = 'open_request',
  EDIT = 'edit',
}
export enum ProductStatus {
  OPEN = 'open',
  CLOSE = 'close',
  PRIVATE = 'private',
}
export enum ProductApproveStatus {
  ACCEPTED = 'accepted',
  REQUESTED = 'requested',
  DENIED = 'denied',
  NOT_READY = 'not_ready',
  READY = 'ready',
}

export enum LinkedSalesChannelStatus {
  REQUESTED = 'requested',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  OPEN = 'open',
  CLOSE = 'close',
}

export enum ProductActivityType {
  HOME = 'home',
  STUDIO = 'studio',
  EXPERIENTIAL = 'experiential',
  ONLINE = 'online',
  DIGITAL = 'digital',
  DELIVERY = 'delivery',
  TICKET = 'ticket',
}

export enum ProductCategory {
  HISTORY = 'history',
  FOREST = 'forest',
  FARM = 'farm',
  PERFORMANCE = 'performance',
  FAMILY = 'family',
  ENGLISH = 'english',
  SCIENCE = 'science',
  PHYSICAL = 'physical',
  ART = 'art',
  LEARNING = 'learning',
  HOBBY = 'hobby',
  ONLINE = 'online',
}

export const PRODUCT_STATUS_TEXT: Record<ProductStatus, StatusText> = {
  open: { text: '판매중', textColor: 'white', backgroundColor: 'green' },
  close: { text: '판매중지', textColor: 'white', backgroundColor: '#aaaaaa' },
  private: { text: '프라이빗', textColor: 'white', backgroundColor: '#e8ac07' },
};

export const PRODUCT_APPROVE_STATUS_TEXT: Record<ProductApproveStatus, StatusText> = {
  accepted: { text: '승인 완료', textColor: 'white', backgroundColor: 'green' },
  requested: { text: '승인 요청', textColor: 'white', backgroundColor: 'orange' },
  denied: { text: '승인 반려', textColor: 'white', backgroundColor: 'red' },
  not_ready: { text: '수정중', textColor: 'white', backgroundColor: 'blue' },
  ready: { text: '수정완료', textColor: 'white', backgroundColor: 'blue' },
};

export const PRODUCT_ACTIVITY_TYPE_TEXT: { [key: string]: { title: string; description: string; Icon: any } } = {
  [ProductActivityType.STUDIO]: {
    title: '스튜디오',
    description: '튜터님이 운영하는 공간으로 방문하는 수업입니다.',
    Icon: OfficeIcon,
  },
  [ProductActivityType.EXPERIENTIAL]: {
    title: '체험수업',
    description: '',
    Icon: ExperienceIcon,
  },
  [ProductActivityType.HOME]: {
    title: '가정방문',
    description: '튜터님이 집으로 오시는 방문수업입니다.',
    Icon: HomeIcon,
  },
  [ProductActivityType.ONLINE]: {
    title: '비대면',
    description: '튜터님과 협의한 시간에 실시간 비대면으로 진행되는 수업입니다.',
    Icon: OnlineIcon,
  },
  [ProductActivityType.DIGITAL]: {
    title: '디지털상품',
    description: 'PDF 등의 디지털 파일을 구매하는 상품입니다.',
    Icon: DownloadIcon,
  },
  [ProductActivityType.TICKET]: {
    title: '티켓상품',
    description: '티켓 상품입니다.',
    Icon: TicketIcon,
  },
  [ProductActivityType.DELIVERY]: {
    title: '배송상품',
    description: '기입하신 주소지로 배송되는 상품입니다.',
    Icon: DeliveryIcon,
  },
};

interface BottomTextBanner {
  show?: boolean;
  iconTemp?: string;
  icon?: string;
  highlightText?: string;
  normalText?: string;
}

export interface Concept {
  conceptTitle?: string;
  conceptData?: string;
  images?: string[] | AttachImage[];
  imagesTemp?: string[];
  idTemp?: string;
}

export interface CommerceFixedOptionItem {
  title: string;
  fee: number;
  essential: boolean;
}

export interface PreferredScheduleConfig {
  startTime: string;
  endTime: string;
  intervalMinutes: 30 | 60;
}

export interface QuestionOption {
  question: string;
  questionType?: string;
  preferredSchedule?: PreferredScheduleConfig;
}

export interface QuestionOptionEditModel extends QuestionOption {
  answer?: string;
}

export interface ProductOption {
  _id?: string;
  productId?: string;
  step: string[];
  type: string; // direct / sequential
  fee?: number;
  inventory?: number;
  index?: number;
  yyyymmdd?: string;
  date?: Date;
  // 옵션별 취소·환불 정책 (향후 확장 예정 항목 포함)
  cancellationPolicy?: {
    immediateCancelDisabled?: boolean;
  };
  createdAt?: Date;
  updatedAt?: Date;
}

export class DiscountInfo {
  discountStartDate?: Date;
  discountEndDate?: Date;
  discountedFee?: number;
}

export class SalesChannelInfo {
  naverStore?: {
    channelProductNo: string;
    originProductNo: string;
    requestedAt?: Date;
    openedAt?: Date;
    status?: LinkedSalesChannelStatus;
    rejectReason?: string;
  };
  kakaoReservation?: {
    productUrl?: string;
    productName?: string;
    requestedAt?: Date;
    openedAt?: Date;
    status?: LinkedSalesChannelStatus;
    rejectReason?: string;
    optionNamePrices?: { key: string; value: number }[];
  };
  benepia?: {
    status?: LinkedSalesChannelStatus; // open, close만 활용
    exclusive?: boolean; // true인 경우 benepia에서만 노출
    /** true면 앱 fee 대신 sellPrice를 베네피아 판매가로 사용 */
    useCustomPrice?: boolean;
    sellPrice?: number;
  };
  ezwel?: {
    gdsCd?: string;
    cspGdsCd?: string; // 기본은 아이고고 상품 _id, 미연동 시 변경 가능
    status?: LinkedSalesChannelStatus;
    exclusive?: boolean;
    purchasePrice?: number;
    sellPrice?: number;
    marginRate?: number;
    /** true면 앱 fee 대신 sellPrice를 이지웰 판매가로 사용 (단독특가) */
    useCustomPrice?: boolean;
    brandCd?: string;
    brandNm?: string;
    mngStdCtgrCd?: string;
    dspStdCtgrCdList?: string[];
    /** true면 sellStrtDtm~sellEndDtm을 HPAS 판매기간으로 전송 */
    useSalePeriod?: boolean;
    sellStrtDtm?: string;
    sellEndDtm?: string;
    taxnKndCd?: string;
    csrcPblcObjYn?: string;
    cnclRfndPossYn?: string;
    gdsAnmtClsCd?: string;
    gdsAnmtCntnList?: Array<{ lyotSeq: number; gdsAnmtCntn: string }>;
    gdsTypDtlCd?: string;
    intgGdsTypCd?: string;
    rdnoStupCd?: string;
    rejectReason?: string;
    requestedAt?: Date;
    openedAt?: Date;
    lastSyncedAt?: Date;
    gdsSellStCd?: string;
    gdsApvStCd?: string;
  };
}

export interface ProductStatistics {
  sido: {
    [key: string]: number;
  };
  sigungu: {
    [key: string]: number;
  };
  birthYear: {
    [key: string]: number;
  };
  age: {
    [key: string]: number;
  };
  totalSalesCount: number;
  reviewCount: number;
  reviewRating: number;
  reviewRate1: number;
  reviewRate2: number;
  reviewRate3: number;
  reviewRate4: number;
  reviewRate5: number;
  updatedAt: Date;
}

export interface LabelInfo {
  name: string;
  source: string;
  createdAt: Date;
}

export interface ProductModel {
  _id: string;
  sellerName: string;
  sellerId: string;
  sellerProfileImage?: string;
  sellerIntroduction?: string;
  title: string;
  saleType: string;
  detailType?: string;
  activityType: ProductActivityType;
  thumbnailImage: string;
  closeText?: string;
  discount?: string;
  originFee: number;
  fee: number;
  classDuration?: number;
  durations?: { hour: number; minute: number; duration: number }[];
  totalNumber: number;
  shippingProduct?: boolean;
  showRegularTime?: boolean;
  regularTime?: number;
  labels: LabelInfo[];
  tags: string[];
  regionText?: string;
  badges: ClassBadge[];
  location: ClassAvailableArea;
  classIntroductionImage?: string;
  classIntroductionImageTemp?: string;
  detailImages: string[];
  detailImagesTemp?: string[];
  detailBodyImages?: string[];
  detailBodyImagesTemp?: string[];
  naverDetailBodyImages?: string[];
  naverDetailBodyImagesTemp?: string[];
  naverTopLinkBanners?: { image: string; url: string }[];
  naverTopLinkBannersTemp?: { image: string; url: string }[];
  ezwelDetailTopImages?: string[];
  ezwelDetailTopImagesTemp?: string[];
  ezwelProductInfoImages?: string[];
  ezwelRefundPolicyImages?: string[];
  detailHtml: string;
  details: {
    disableDetailImage?: boolean;
    disableClassIntroduction?: boolean;
    disableRecommendText?: boolean;
    disableConcepts?: boolean;
    disablePreparationText?: boolean;
    detailImage?: string;
    detailImageTemp?: string;
    introductions?: { title?: string; description?: string }[];
    concepts?: Concept[];
    preparationText?: string;
    recommendText?: string;
    youtubeId?: string;
  };
  refundPolicyText?: string;
  shippingPolicyText?: string;
  classTermsText?: string;
  refundPolicies?: string[];
  applyGroupDiscount?: boolean;
  ageMin: number;
  ageMax: number;
  studentsMin?: number;
  studentsMax?: number;
  categories: string[];
  bottomTextBanner?: BottomTextBanner;
  display?: boolean;
  soldout?: boolean;
  isClosed?: boolean;
  totalInventory?: number;
  shippingFee?: number;
  memoRequired?: boolean;
  memoComment?: string;
  linkedSalesChannel?: SalesChannelInfo;
  sellerComment?: string;
  sellerShortComment?: string;
  status?: LegacyProductStatus | ProductStatus;
  impressions?: { value: number; unit: string };
  approveStatus?: ProductApproveStatus;
  optionType?: string; // direct / sequential / sequential_with_direct
  purchaseQuantityOptions: CommerceFixedOptionItem[];
  productOptions?: ProductOption[];
  optionLabels?: string[];
  optionDescription?: string;
  questionOptions?: QuestionOption[];
  meta: any;
  priority?: number;
  applyPeriodDiscount?: boolean;
  periodDiscountExcludeChannels?: string[]; // naver, kakao, benepia, app
  discountInfo?: DiscountInfo;
  minStudents?: number;
  maxStudents?: number;
  approvedAt: Date;
  createdAt: Date;
  updatedAt: Date;
  deniedReason: string;
  isTemp?: boolean;
  favoritedCount?: number;
  naverStoreProduct?: NaverCommerceProduct;
  autoCloseEnabled?: boolean;
  autoCloseDuration?: number;
  memberships?: Partial<Membership>[];
  appOnly?: boolean;
  /** true면 캘린더 옵션 UI 대신 기존 옵션 선택 모달 강제 */
  forceOptionModal?: boolean;
  gpsLocation?: { type: string; coordinates: [number, number] };
  programReportAvailable?: boolean;
  programReportItems?: IntelligenceType[];
  enableLimitPurchase?: boolean;
  limitPurchaseCount?: number;
  statistics?: ProductStatistics;
  statistics1week?: ProductStatistics;
  statistics3month?: ProductStatistics;
  viewCount?: number;
  firstApprovedAt?: Date;
  inventory?: number;
  commissionRateId?: string;
  rootCategories?: ProductCategory[];
  coupangProduct?: {
    title: string;
    products: {
      linkUrl: string;
      thumbnailImage: string;
      thunbnailAltText: string;
      width: number;
      height: number;
    }[];
  };
  baseRecommendationScore?: number;
}

export interface ProductSearchQuery {
  keyword?: string;
  saleType?: string;
  sort?: string;
  activityType?: string;
  category?: string;
  age?: string;
  region?: string;
  status?: string;
  displayStatus?: string;
  stockStatus?: string;
  page?: number;
  size?: number;
  sellerId?: string;
  board?: string;
  commissionRateId?: string;
  coordinates?: string;
  applyPeriodDiscount?: boolean;
}

export interface ReviewData {
  rate1: number;
  rate2: number;
  rate3: number;
  rate4: number;
  rate5: number;
  comment: string;
}

export interface CreateReviewBody extends ReviewData {
  paymentStatementId: string;
  productId: string;
}

export interface UpdateReviewBody extends ReviewData {
  reviewId: string;
  paymentStatementId: string;
  productId: string;
}

export interface ProductInventoryClosingLog {
  _id: string;
  type: string; // naver, comerce
  sellerId: string;
  sellerName: string;
  productName: string;
  origin: any;
  new: any;
  deletedOptions: any;
  updateResult?: any;
  createdAt: Date;
}
